import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { createSelector } from '@reduxjs/toolkit';

import { useDispatch } from 'store/configureStore';

import { fetchPaymentMethods } from 'actions';
import type { CardSettings } from 'models/offer';
import type { CardWithCVVRequirement } from 'models/paymentMethod';
import type { RootState } from 'reducers/types';
import { getCreditCards, isFetching } from 'selectors';
import { isCardValid } from 'utils/cardSettings/cardSettings';

export const getCreditCardData = createSelector(
  [getCreditCards, (state: RootState) => isFetching(state, 'user')],
  (creditCards, isFecthingCreditCards) => ({
    creditCards: creditCards || [],
    isFecthingCreditCards: isFecthingCreditCards || !creditCards,
    hasCreditCards: (creditCards || []).length > 0,
  }),
);

export function useCreditCards(
  cardSettings?: CardSettings,
  currentToken?: string,
) {
  const { creditCards, isFecthingCreditCards, hasCreditCards } =
    useSelector(getCreditCardData);
  const dispatch = useDispatch();

  const validCards = creditCards?.filter(card => {
    return isCardValid(card.details.scheme, card.details.type, cardSettings);
  });
  const filteredCards = currentToken
    ? validCards?.reduce((acc, current) => {
        if (current.token === currentToken) {
          return [{ ...current, requiresCVV: false }, ...acc];
        }
        acc.push({ ...current, requiresCVV: true });

        return acc;
      }, [] as CardWithCVVRequirement[])
    : validCards?.map(card => ({ ...card, requiresCVV: true }));

  useEffect(() => {
    dispatch(fetchPaymentMethods());
  }, [dispatch]);

  return {
    isFecthingCreditCards,
    creditCards: filteredCards,
    hasCreditCards,
  };
}
